import { Button, Select, Spinner, TextField } from '@cimpress/react-components';
import type { CSSProperties } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CardBuilder,
    Content,
    Control,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    type Card,
} from '../../../Components';
import { useGenerateLogoV4Params } from '../../../Components/InspirationGenerate/useGenerateLogoV4Params';
import type { SelectorOption } from '../../../Components/common';
import { postGenerateLogoV4 } from '../../../api/generateLogoV4';
import { MEDIA_TYPE_OPTIONS, IMAGE_COUNT_OPTIONS } from '../../../common/constants';
import { useCancelableEffect } from '../../../tools';
import { CULTURE_VALUE_OPTIONS, PRIORITY_OPTIONS, STYLE_OPTIONS } from './constants';

const LOADING_CONTAINER_STYLE: CSSProperties = {
    textAlign: 'center' as const,
    padding: '20px',
};

const LOADING_TEXT_STYLE: CSSProperties = {
    marginTop: '10px',
};

const SPACER_STYLE: CSSProperties = {
    marginBottom: '1px',
};

const LOGO_IMAGE_STYLE: CSSProperties = {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: '8px',
    cursor: 'pointer',
};

const LOGO_CARD_CONTENT_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
};

const EDIT_IMAGE_BASE_URL = '/edit-image';

export const GenerateLogoV4Page = () => {
    const navigate = useNavigate();
    const {
        prompt,
        tagLine,
        businessName,
        industry,
        businessDescription,
        style,
        mediaType,
        imageCount,
        priority,
        culture,
        setPrompt,
        setTagLine,
        setBusinessName,
        setIndustry,
        setBusinessDescription,
        setStyle,
        setMediaType,
        setImageCount,
        setPriority,
        setCulture,
    } = useGenerateLogoV4Params();
    const [resultRow, setResultRow] = useState(PreviewRow());
    const [isLoading, setIsLoading] = useState(false);
    const [callGenerate, setCallGenerate] = useState(false);

    const onEditImage = useCallback(
        (logoUrl: string, prompt?: string) => {
            const params = new URLSearchParams();
            params.set('imageUrl', logoUrl);
            if (prompt) {
                params.set('description', prompt);
            }
            navigate(`${EDIT_IMAGE_BASE_URL}?${params.toString()}`);
        },
        [navigate],
    );

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (callGenerate) {
                    setIsLoading(true);

                    const imageResultCards: Card[] = [];

                    const resultImagePromises: Promise<{ imageUrl: string; prompt: string }>[] = [];

                    for (let i = 0; i < parseInt(imageCount); i++) {
                        const params: any = {
                            businessName: businessName ?? '',
                            signal,
                        };

                        if (prompt) params.prompt = prompt;
                        if (tagLine) params.tagLine = tagLine;
                        if (industry) params.industry = industry;
                        if (businessDescription) params.businessDescription = businessDescription;
                        if (style) params.style = style;
                        if (mediaType) params.mediaType = mediaType;
                        if (priority) params.priority = priority;
                        if (culture) params.culture = culture.value;

                        const resultImagePromise = postGenerateLogoV4(params);
                        resultImagePromises.push(resultImagePromise);
                    }

                    // Wait for all promises to settle
                    const generateLogoResults = await Promise.allSettled(resultImagePromises);

                    // Build the cards using resolved logo results
                    for (const generateLogoResult of generateLogoResults) {
                        let imageResultCard;
                        if (generateLogoResult.status === 'fulfilled') {
                            imageResultCard = CardBuilder.ofType('childComponent')
                                .withTitle(`Logo`)
                                .withChildComponent(() => (
                                    <div style={LOGO_CARD_CONTENT_STYLE}>
                                        <img
                                            src={generateLogoResult.value.imageUrl}
                                            alt="Generated Logo"
                                            style={LOGO_IMAGE_STYLE}
                                            onClick={() => window.open(generateLogoResult.value.imageUrl, '_blank')}
                                        />
                                        <Button
                                            onClick={() =>
                                                onEditImage(
                                                    generateLogoResult.value.imageUrl,
                                                    generateLogoResult.value.prompt.replace(/^"(.*)"$/, '$1'),
                                                )
                                            }
                                        >
                                            Edit Image
                                        </Button>
                                    </div>
                                ))
                                .build();
                        } else {
                            imageResultCard = CardBuilder.ofType('documentUrl')
                                .withTitle('Result Document')
                                .withLoader(async () => {
                                    throw generateLogoResult.reason;
                                })
                                .build();
                        }

                        imageResultCards.push(imageResultCard);
                        setResultRow(PreviewRow(...imageResultCards));
                    }

                    setIsLoading(false);
                    setCallGenerate(false);
                }
            })();
        },
        [
            callGenerate,
            prompt,
            tagLine,
            businessName,
            industry,
            businessDescription,
            style,
            mediaType,
            imageCount,
            priority,
            culture,
            onEditImage,
        ],
    );

    const onCultureChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setCulture(selected);
    };

    const onMediaTypeChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setMediaType(selected.value);
    };

    const onImageCountChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setImageCount(selected.value);
    };

    const onPriorityChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setPriority(selected.value);
    };

    function onClickGenerate() {
        setCallGenerate(true);
    }

    return (
        <Page>
            <Page.Title>Generate Logo V4</Page.Title>

            <Page.Controls>
                <Control spacing="standard">
                    <Control.Title>Business Name</Control.Title>
                    <TextField
                        label="Business Name"
                        onChange={(e) => setBusinessName(e.target.value)}
                        value={businessName ?? ''}
                        required
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Industry</Control.Title>
                    <TextField label="Industry" onChange={(e) => setIndustry(e.target.value)} value={industry ?? ''} />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Business Description</Control.Title>
                    <TextField
                        label="Business Description"
                        onChange={(e) => setBusinessDescription(e.target.value)}
                        value={businessDescription ?? ''}
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Tagline</Control.Title>
                    <TextField label="Tagline" onChange={(e) => setTagLine(e.target.value)} value={tagLine ?? ''} />
                </Control>

                <div style={SPACER_STYLE} />

                <Control spacing="compact">
                    <Control.Title>Prompt</Control.Title>
                    <TextField label="Prompt" onChange={(e) => setPrompt(e.target.value)} value={prompt ?? ''} />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Style</Control.Title>
                    <Select
                        onChange={(selected) => selected && setStyle(selected.value)}
                        value={STYLE_OPTIONS.find((opt) => opt.value === style)}
                        options={STYLE_OPTIONS}
                        label="Style"
                    />
                </Control>

                <div style={SPACER_STYLE} />

                <Control spacing="standard">
                    <Control.Title>Priority</Control.Title>
                    <Select
                        onChange={onPriorityChange}
                        value={PRIORITY_OPTIONS.find((opt: SelectorOption) => opt.value === priority)}
                        options={PRIORITY_OPTIONS}
                        label="Priority"
                    />
                </Control>

                <div style={SPACER_STYLE} />

                <Control spacing="compact">
                    <Control.Title>Media Type</Control.Title>
                    <Select
                        onChange={onMediaTypeChange}
                        value={MEDIA_TYPE_OPTIONS.find((opt: SelectorOption) => opt.value === mediaType)}
                        options={MEDIA_TYPE_OPTIONS}
                        label="Media Type"
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Culture</Control.Title>
                    <Select
                        onChange={onCultureChange}
                        value={culture}
                        options={CULTURE_VALUE_OPTIONS}
                        label="Culture"
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Image Count</Control.Title>
                    <Select
                        onChange={onImageCountChange}
                        value={IMAGE_COUNT_OPTIONS.find((opt: SelectorOption) => opt.value === imageCount.toString())}
                        options={IMAGE_COUNT_OPTIONS}
                        label="Image Count"
                    />
                </Control>

                <Control spacing="standard">
                    <Button onClick={onClickGenerate} disabled={!businessName || callGenerate}>
                        Generate
                    </Button>
                </Control>
            </Page.Controls>

            <Page.Content>
                {isLoading ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <div style={LOADING_CONTAINER_STYLE}>
                                <Spinner />
                                <p style={LOADING_TEXT_STYLE}>Generating logo...</p>
                            </div>
                        </Content.Section>
                    </Content.Card>
                ) : (
                    <PreviewSection
                        introduction={
                            'Fill in the Business Name and click Generate. Once a logo is generated, you can click "Edit Image" to modify it using the Edit Image tool.'
                        }
                        showIntro={!resultRow.length}
                        rows={PreviewRows(resultRow)}
                    />
                )}
            </Page.Content>
        </Page>
    );
};

export default GenerateLogoV4Page;
